import { useState, type FormEvent } from 'react';
import { ShopError, api, type ShopVariant } from '../api';

/**
 * Hlídač u varianty: dostupnost („dej vědět, až bude skladem") nebo cena
 * („dej vědět, až zlevní"). Ukládá se jen e-mail navázaný na variantu a druh
 * hlídání – žádné přihlášení. Formulář je klíčovaný id varianty, takže
 * přepnutí na jinou variantu ho začne s čistým stavem.
 */
export function WatchForm({
  slug,
  variant,
  kind = 'availability',
}: {
  slug: string;
  variant: ShopVariant;
  kind?: 'availability' | 'price';
}) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const isPrice = kind === 'price';

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (status === 'sending') return;

    const trimmed = email.trim();
    if (trimmed === '') {
      setStatus('error');
      setMessage('Zadej prosím e-mail.');
      return;
    }

    setStatus('sending');
    try {
      const result = await api.watchAvailability(slug, { variantId: variant.id, email: trimmed, kind });
      setStatus('done');
      if (result.duplicate) {
        setMessage(
          isPrice
            ? `Cenu téhle varianty už hlídáme — dáme vědět na ${trimmed}.`
            : `Tuhle variantu už hlídáme — dáme vědět na ${trimmed}.`,
        );
      } else {
        setMessage(
          isPrice
            ? `Hotovo. Jakmile „${variant.name}" zlevní, napíšeme na ${trimmed}.`
            : `Hotovo. Jakmile bude „${variant.name}" skladem, napíšeme na ${trimmed}.`,
        );
      }
    } catch (error) {
      setStatus('error');
      setMessage(
        error instanceof ShopError
          ? error.message
          : 'Nepodařilo se to uložit. Zkus to prosím za chvíli.',
      );
    }
  }

  if (status === 'done') {
    return (
      <div className="watch watch-done" role="status">
        <strong>Platí!</strong> {message}
      </div>
    );
  }

  return (
    <form className="watch" onSubmit={submit} noValidate>
      <p className="watch-lead">
        {isPrice ? (
          <>
            <strong>Zlevní to?</strong> Nech e-mail a dáme ti vědět, jakmile tahle varianta zlevní.
          </>
        ) : (
          <>
            <strong>Dojde znovu?</strong> Nech e-mail a dáme ti vědět, jakmile bude tahle varianta
            skladem.
          </>
        )}
      </p>
      <div className="watch-row">
        <input
          type="email"
          className="watch-input"
          placeholder="vas@email.cz"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            if (status === 'error') setStatus('idle');
          }}
          aria-label={isPrice ? 'E-mail pro upozornění na zlevnění' : 'E-mail pro upozornění na dostupnost'}
          autoComplete="email"
        />
        <button type="submit" className="button primary" disabled={status === 'sending'}>
          {status === 'sending' ? 'Ukládám…' : isPrice ? 'Hlídat cenu' : 'Hlídat dostupnost'}
        </button>
      </div>
      {status === 'error' && <p className="watch-error">{message}</p>}
      <p className="watch-note">Napíšeme jen jednou. Žádný newsletter.</p>
    </form>
  );
}
